import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { t } from 'ember-intl';
import OverrideIndicator from 'lumen/components/hotel/pricing-assistant/override-indicator';
import type { Period } from 'lumen/models';
import type { RateGroup } from 'lumen/models/pa-subscription-meta-info';
import { RESTRICTION_MODEL_CONTEXTS } from 'lumen/utils/constants/channel-pricing-constants';
import { FEATURE_TOGGLES } from 'lumen/utils/constants/feature-toggle-constants';
import {
  formatRateGroupLabel,
  groupRateGroupsByMinLos,
  type RateGroupMinLos,
} from 'lumen/utils/pricing-assistant/rate-group-restriction-utils';
import {
  formatMinLosRecommendation,
  formatMinLosValue,
} from 'lumen/utils/pricing-assistant/restriction-display-utils';

import styles from './pricing-assistant-table-tooltip.module.css';

interface RateGroupRow {
  formattedValue: string;
  hasOverride: boolean;
  isLoading: boolean;
  label: string;
}

interface FailedRateGroupRow {
  count: number;
  formattedValue: string;
}

interface HotelPricingAssistantTableTooltipsTooltipStayRestrictionsTableSignature {
  Args: {
    period: Period;
  };
}

export default class HotelPricingAssistantTableTooltipsTooltipStayRestrictionsTable extends Component<HotelPricingAssistantTableTooltipsTooltipStayRestrictionsTableSignature> {
  @service declare featureToggle: Services['feature-toggle'];
  @service declare intl: Services['intl'];
  @service('pricing-assistant-data-request-service')
  declare pricingAssistantDataRequestService: Services['pricing-assistant-data-request-service'];

  get configuredRateGroups(): RateGroup[] {
    if (
      !this.featureToggle.hasFeatureToggle(
        FEATURE_TOGGLES.PRICING_MANAGER_DEEP_DISCOUNT,
      )
    ) {
      return [];
    }

    return this.pricingAssistantDataRequestService.configuredRateGroups;
  }

  get failedRateGroupRows(): FailedRateGroupRow[] {
    return groupRateGroupsByMinLos(this.failedRateGroups).map((row) => ({
      count: row.rateGroupIds.length,
      formattedValue: formatMinLosValue({ intl: this.intl, value: row.value }),
    }));
  }

  get failedRateGroups(): RateGroupMinLos[] {
    return this.configuredRateGroups.flatMap((rateGroup) => {
      const value = this.args.period.failedMinLosValue({
        context: RESTRICTION_MODEL_CONTEXTS.RATE_GROUP_LEVEL,
        rateGroupId: rateGroup.rateGroupId,
      });

      if (value == null) {
        return [];
      }

      return {
        hasOverride: false,
        isLoading: false,
        rateGroupId: rateGroup.rateGroupId,
        value,
      };
    });
  }

  get failedRestrictionCount(): number {
    return this.failedRateGroups.length + 1;
  }

  get formattedHotelMinLosRecommendation(): string {
    return formatMinLosRecommendation({
      displayedValue: this.args.period.effectiveHotelMinLosWithOverride,
      intl: this.intl,
      recommendedValue:
        this.args.period.hotelLevelRestrictionRecommendation?.minLosValue,
    });
  }

  get formattedHotelMinLosValue(): string {
    return formatMinLosValue({
      intl: this.intl,
      value: this.args.period.effectiveHotelMinLosWithOverride,
    });
  }

  /* The BE recalculates the hotel MinLOS from the rate groups, so it is stale
     while any of them is pushing. */
  get hasRateGroupMinLosActionInProgress(): boolean {
    return this.rateGroupRows.some((row) => row.isLoading);
  }

  get rateGroupRows(): RateGroupRow[] {
    const entries = this.configuredRateGroups.map((rateGroup) =>
      this.args.period.rateGroupMinLos(rateGroup.rateGroupId),
    );

    return groupRateGroupsByMinLos(entries).map((row) => ({
      formattedValue: formatMinLosValue({ intl: this.intl, value: row.value }),
      hasOverride: row.hasOverride,
      isLoading: row.isLoading,
      label: formatRateGroupLabel(
        row.rateGroupIds.map((rateGroupId) => this.rateGroupName(rateGroupId)),
        this.intl,
      ),
    }));
  }

  get shouldShowHotelRecommendedRow(): boolean {
    return this.showHotelMinLosOverride;
  }

  /* There is no hotel-level push, so a rate group failure also fails the hotel. No
     value is shown, to avoid duplicating the BE derivation. */
  get showFailedHotel(): boolean {
    return this.failedRateGroups.length > 0;
  }

  /* showFailedHotel also gates the row: the period reports a failure for any rate
     group, but only the configured ones have a value to list, so without this a
     failure on an unconfigured group renders the header above an empty list. The
     hotel line shows whenever there is something to list, so it is the gate, and the
     count can always include the hotel. */
  get showFailedRestriction(): boolean {
    return (
      this.args.period.shouldShowStayRestrictionException &&
      this.showFailedHotel
    );
  }

  get showHotelMinLosOverride(): boolean {
    return this.args.period.showHotelMinLosOverride;
  }

  /* Rate group ids are opaque uuids, so rows are labelled with the name the hotel
     gave the group in CEMA, falling back to the id rather than rendering blank. */
  rateGroupName(rateGroupId: string): string {
    const rateGroup = this.configuredRateGroups.find(
      (group) => group.rateGroupId === rateGroupId,
    );

    return rateGroup?.name || rateGroupId;
  }

  <template>
    <table
      class="default-table {{styles.table}} {{styles.wrapping-names-table}}"
      data-test-stay-restrictions-section
    >
      <thead>
        <tr>
          <th class="{{styles.table-header}} text-200--bold">
            {{t "pricing_assistant.stay_restrictions.header"}}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td class="{{styles.table-row-cell}} text-200--regular">
            {{#if this.hasRateGroupMinLosActionInProgress}}
              <div
                class="loading-pulse w-5/6"
                data-test-id="stay-restrictions-hotel-loading"
              ></div>
              <div class="loading-pulse w-1/6"></div>
            {{else}}
              <p class="text-200--regular">
                {{t "general.hotel"}}
              </p>
              <div class={{styles.value-row}}>
                <p
                  class="text-200--regular"
                  data-test-stay-restrictions-hotel-value
                >
                  {{this.formattedHotelMinLosValue}}
                </p>
                {{#if this.showHotelMinLosOverride}}
                  <span class={{styles.override-indicator}}>
                    <OverrideIndicator @variant="automation-off" />
                  </span>
                {{/if}}
              </div>
            {{/if}}
          </td>
        </tr>
        {{! Row for recommended MinLOS when override is active }}
        {{#if this.shouldShowHotelRecommendedRow}}
          <tr>
            <td
              class="{{styles.table-row-cell}}
                {{styles.indented-row}}
                text-200--regular"
            >
              <p class="text-200--regular">
                {{t "pricing_assistant.stay_restrictions.recommended"}}
              </p>
              {{#if this.hasRateGroupMinLosActionInProgress}}
                <div class="loading-pulse w-1/6"></div>
              {{else}}
                <div class={{styles.value-row}}>
                  <p
                    class="text-200--regular"
                    data-test-id="stay-restrictions-hotel-recommended-value"
                  >
                    {{this.formattedHotelMinLosRecommendation}}
                  </p>
                </div>
              {{/if}}
            </td>
          </tr>
        {{/if}}
        {{#each this.rateGroupRows as |rateGroupRow|}}
          <tr data-test-rate-group-row={{rateGroupRow.label}}>
            <td class="{{styles.table-row-cell}} text-200--regular">
              {{#if rateGroupRow.isLoading}}
                <div
                  class="loading-pulse w-5/6"
                  data-test-id="stay-restrictions-rate-group-loading"
                ></div>
                <div class="loading-pulse w-1/6"></div>
              {{else}}
                <p
                  class="text-200--regular {{styles.wrapping-cell}}"
                  data-test-rate-group-label
                >
                  {{rateGroupRow.label}}
                </p>
                <div class="flex flex-col items-end {{styles.fixed-cell}}">
                  <div class={{styles.value-row}}>
                    <p class="text-200--regular" data-test-rate-group-value>
                      {{rateGroupRow.formattedValue}}
                    </p>
                    {{#if rateGroupRow.hasOverride}}
                      <span
                        class={{styles.override-indicator}}
                        data-test-rate-group-override-indicator
                      >
                        <OverrideIndicator @variant="automation-off" />
                      </span>
                    {{/if}}
                  </div>
                </div>
              {{/if}}
            </td>
          </tr>
        {{/each}}
        {{! Failed restriction(s) row when a MinLOS push has failed }}
        {{#if this.showFailedRestriction}}
          <tr>
            <td
              class="{{styles.table-row-cell}} text-200--regular"
              data-test-failed-restriction-row
            >
              {{#if this.hasRateGroupMinLosActionInProgress}}
                <div
                  class="loading-pulse w-5/6"
                  data-test-id="failed-restriction-loading"
                ></div>
                <div class="loading-pulse w-1/6"></div>
              {{else}}
                <p class="text-200--regular {{styles.fixed-cell}}">
                  {{t
                    "pricing_manager.failed_restriction"
                    count=this.failedRestrictionCount
                  }}
                </p>
                <div class="flex flex-col items-end {{styles.wrapping-cell}}">
                  {{#if this.showFailedHotel}}
                    <p
                      class="text-200--regular"
                      data-test-failed-restriction-hotel
                    >
                      {{t "general.hotel"}}
                    </p>
                  {{/if}}
                  {{#each this.failedRateGroupRows as |failedRateGroupRow|}}
                    <p class="text-200--regular">
                      {{t
                        "pricing_assistant.stay_restrictions.rate_group_count"
                        count=failedRateGroupRow.count
                      }}:
                      <span data-test-failed-restriction-rate-group-value>
                        {{failedRateGroupRow.formattedValue}}
                      </span>
                    </p>
                  {{/each}}
                </div>
              {{/if}}
            </td>
          </tr>
        {{/if}}
      </tbody>
    </table>
  </template>
}
